import { z } from 'zod';
import { BrowserDialogSchema, CursorSchema, NodeUpdateSchema } from './browser-tools.js';
import { BrowserRuntimeSchema, ErrorInfoSchema, IdSchema, InputEventSchema, LocaleSchema, ProxyConfigSchema, TabInfoSchema, TimezoneSchema, WindowSizeSchema, } from './common.js';
import { StreamModeSchema, StreamParamsSchema } from './modes.js';
// Node channel: VOS Bridge Node <-> server over /ws/node (Bearer node credential).
const Req = z.object({ id: IdSchema });
export const BrowserProfileSpecSchema = z.object({
    profileId: IdSchema,
    /** Initial URL for the first tab when the profile is fresh (or when no tabs are open) */
    initialUrl: z.string().optional(),
    size: WindowSizeSchema,
    proxy: ProxyConfigSchema.optional(),
    /** Override node default; undefined = use node setting */
    headless: z.boolean().optional(),
    /** BCP 47 locale to present; should match the proxy's exit region. Unset = system */
    locale: LocaleSchema.optional(),
    /** IANA time zone to present; should match the proxy's exit region */
    timezoneId: TimezoneSchema.optional(),
});
export const ServerToNodeSchema = z.discriminatedUnion('t', [
    Req.extend({ t: z.literal('node.update'), force: z.boolean() }),
    Req.extend({ t: z.literal('clipboard.read'), profileId: IdSchema, tabId: IdSchema }),
    Req.extend({
        t: z.literal('dialog.respond'),
        profileId: IdSchema,
        tabId: IdSchema,
        dialogId: IdSchema,
        accept: z.boolean(),
        promptText: z.string().max(20000).optional(),
    }),
    z.object({
        t: z.literal('welcome'),
        nodeId: IdSchema,
        nodeName: z.string(),
        serverTime: z.number(),
    }),
    Req.extend({ t: z.literal('browser.open'), spec: BrowserProfileSpecSchema }),
    Req.extend({ t: z.literal('browser.close'), profileId: IdSchema }),
    Req.extend({ t: z.literal('profile.egressIp'), profileId: IdSchema }),
    Req.extend({ t: z.literal('profile.deleteData'), profileId: IdSchema }),
    Req.extend({ t: z.literal('tab.create'), profileId: IdSchema, url: z.string() }),
    Req.extend({ t: z.literal('tab.close'), profileId: IdSchema, tabId: IdSchema }),
    Req.extend({ t: z.literal('tab.activate'), profileId: IdSchema, tabId: IdSchema }),
    Req.extend({
        t: z.literal('tab.navigate'),
        profileId: IdSchema,
        tabId: IdSchema,
        url: z.string(),
    }),
    Req.extend({ t: z.literal('tab.back'), profileId: IdSchema, tabId: IdSchema }),
    Req.extend({ t: z.literal('tab.forward'), profileId: IdSchema, tabId: IdSchema }),
    Req.extend({ t: z.literal('tab.reload'), profileId: IdSchema, tabId: IdSchema }),
    Req.extend({ t: z.literal('tab.list'), profileId: IdSchema }),
    Req.extend({
        t: z.literal('stream.start'),
        profileId: IdSchema,
        tabId: IdSchema,
        stream: StreamParamsSchema,
    }),
    Req.extend({ t: z.literal('stream.stop'), profileId: IdSchema, tabId: IdSchema }),
    Req.extend({ t: z.literal('window.resize'), profileId: IdSchema, size: WindowSizeSchema }),
    z.object({ t: z.literal('input'), profileId: IdSchema, event: InputEventSchema }),
    Req.extend({
        t: z.literal('file.begin'),
        profileId: IdSchema,
        tabId: IdSchema,
        chooserId: IdSchema,
        files: z
            .array(z.object({
            name: z.string().min(1).max(255),
            size: z.number().int().min(0),
            mime: z.string().max(128),
        }))
            .min(1)
            .max(20),
    }),
    Req.extend({
        t: z.literal('file.end'),
        profileId: IdSchema,
        tabId: IdSchema,
        chooserId: IdSchema,
    }),
    z.object({
        t: z.literal('file.cancel'),
        profileId: IdSchema,
        tabId: IdSchema,
        chooserId: IdSchema,
    }),
    z.object({ t: z.literal('ping'), ts: z.number() }),
]);
export const NodeHelloSchema = z.object({
    t: z.literal('hello'),
    protocolVersion: z.number().int(),
    nodeVersion: z.string(),
    os: z.string(),
    hostname: z.string().max(128).optional(),
    capabilities: z.object({
        browserTools: z.boolean().optional(),
        remoteUpdate: z.boolean().optional(),
        modes: z.array(StreamModeSchema).min(1),
        chromeAvailable: z.boolean(),
        chromeVersion: z.string().optional(),
    }),
    settings: z.object({
        headless: z.boolean(),
    }),
    /** Profiles whose Chrome runtime is currently open on this node (survives server restarts) */
    openProfiles: z
        .array(z.object({
        profileId: IdSchema,
        tabs: z.array(TabInfoSchema),
        dialogs: z.array(BrowserDialogSchema).optional(),
    }))
        .default([]),
    update: NodeUpdateSchema.optional(),
});
export const ReplySchema = z.discriminatedUnion('ok', [
    z.object({
        t: z.literal('reply'),
        id: IdSchema,
        ok: z.literal(true),
        data: z.unknown().optional(),
    }),
    z.object({ t: z.literal('reply'), id: IdSchema, ok: z.literal(false), error: ErrorInfoSchema }),
]);
export const NodeToServerSchema = z.union([
    NodeHelloSchema,
    ReplySchema,
    z.discriminatedUnion('t', [
        z.object({ t: z.literal('node.update.state'), update: NodeUpdateSchema }),
        z.object({ t: z.literal('dialog.opened'), profileId: IdSchema, dialog: BrowserDialogSchema }),
        z.object({
            t: z.literal('dialog.closed'),
            profileId: IdSchema,
            tabId: IdSchema,
            dialogId: IdSchema,
        }),
        z.object({
            t: z.literal('cursor.changed'),
            profileId: IdSchema,
            tabId: IdSchema,
            cursor: CursorSchema,
        }),
        z.object({
            t: z.literal('browser.state'),
            profileId: IdSchema,
            runtime: BrowserRuntimeSchema,
            error: ErrorInfoSchema.optional(),
        }),
        z.object({ t: z.literal('tabs.changed'), profileId: IdSchema, tabs: z.array(TabInfoSchema) }),
        z.object({
            t: z.literal('tab.navigated'),
            profileId: IdSchema,
            tabId: IdSchema,
            url: z.string(),
            title: z.string().optional(),
            /** true when the node blocked a disallowed navigation */
            blocked: z.boolean().default(false),
        }),
        z.object({
            t: z.literal('stream.state'),
            profileId: IdSchema,
            tabId: IdSchema,
            state: z.enum(['active', 'paused', 'stopped']),
            reason: z.enum(['inactive_tab', 'minimized', 'no_viewers', 'error']).optional(),
        }),
        z.object({
            t: z.literal('filechooser.opened'),
            profileId: IdSchema,
            tabId: IdSchema,
            chooserId: IdSchema,
            multiple: z.boolean(),
        }),
        z.object({
            t: z.literal('filechooser.closed'),
            profileId: IdSchema,
            tabId: IdSchema,
            chooserId: IdSchema,
        }),
        z.object({ t: z.literal('settings.changed'), settings: z.object({ headless: z.boolean() }) }),
        z.object({
            t: z.literal('log'),
            level: z.enum(['info', 'warn', 'error']),
            message: z.string(),
            profileId: IdSchema.optional(),
        }),
        z.object({ t: z.literal('pong'), ts: z.number() }),
    ]),
]);
/** Typed reply payloads for specific requests. */
export const BrowserOpenedDataSchema = z.object({ tabs: z.array(TabInfoSchema) });
export const TabCreatedDataSchema = z.object({ tab: TabInfoSchema });
export const TabListDataSchema = z.object({ tabs: z.array(TabInfoSchema) });
export const EgressIpDataSchema = z.object({
    ip: z.string().optional(),
    error: z.string().optional(),
});
export const WindowResizedDataSchema = z.object({ size: WindowSizeSchema });
//# sourceMappingURL=node.js.map